import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { readInstantCache, writeInstantCache } from '../utils/instantCache';
import { toast } from 'sonner';
import { X, DownloadSimple, MagnifyingGlass, ArrowLeft, ArrowsClockwise } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import ExcelExportPreviewModal from './ExcelExportPreviewModal';
import ClearReportRangeButton from './ClearReportRangeButton';
import { useAuth } from '../contexts/AuthContext';
import { useMBList } from '../hooks/useMBList';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Feature 8: Withdraw Report — global table + per-client drill-down + Excel/PDF export.
 * View: 'list' (all clients) | 'client' (single client history)
 *
 * Row click behaviour (user request): clicking on any row jumps to the exact
 * chat where that deposit/withdraw happened, deep-linking to the underlying
 * message so admins/handlers can review context in a single tap.
 */
const WithdrawReportModal = ({ open, onClose, actorRole }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [view, setView] = useState('list');
  const [activeCustomer, setActiveCustomer] = useState(null); // { id, username }
  const [status, setStatus] = useState('all'); // all|paid|pending|cancelled
  const [rangeType, setRangeType] = useState('today'); // today|yesterday|weekly|monthly|custom — default TODAY
  const [customMode, setCustomMode] = useState('single'); // single | range
  const [mbSetup, setMbSetup] = useState('all'); // all|MB7|MB8|MB9|MB10|MB11
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [rows, setRows] = useState(() => readInstantCache('withdraw_report', 'all') || []);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [fullView, setFullView] = useState(false); // Excel-like full-screen table view
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  const buildParams = () => {
    const p = new URLSearchParams();
    if (status !== 'all') p.set('status', status);
    if (rangeType === 'today') p.set('range', 'today');
    else if (rangeType === 'yesterday') p.set('range', 'yesterday');
    else if (rangeType === 'weekly') p.set('range', 'weekly');
    else if (rangeType === 'monthly') p.set('range', 'monthly');
    else if (rangeType === 'custom') {
      p.set('range', 'custom');
      if (customMode === 'single') {
        if (fromDate) { p.set('from_date', fromDate); p.set('to_date', fromDate); }
      } else {
        if (fromDate) p.set('from_date', fromDate);
        if (toDate) p.set('to_date', toDate);
      }
    }
    if (mbSetup && mbSetup !== 'all') p.set('mb_setup', mbSetup);
    if (view === 'client' && activeCustomer?.id) p.set('customer_id', activeCustomer.id);
    if (user?.id) p.set('user_id', user.id);
    return p;
  };

  // Point 28 — a handler with an MB assignment only gets buttons for their
  // own MB(s); admins and unassigned handlers keep seeing every MB.
  const liveMBs = useMBList();
  const mbOptions = user?.role !== 'super_admin' && user?.assigned_mbs?.length > 0
    ? ['all', ...user.assigned_mbs]
    : ['all', ...liveMBs];

  const load = async (opts = {}) => {
    // `background` refresh keeps the table visible instead of showing "Loading…"
    if (!opts.background) setLoading(true);
    try {
      // Session-48h — when the "enquiry" tab is active, hit the dedicated
      // /inquiry-entries/report endpoint (different collection, different
      // column shape) instead of the withdraw report.
      const url = status === 'enquiry'
        ? `${API}/inquiry-entries/report?${buildParams().toString()}`
        : `${API}/withdraw-entries/report?${buildParams().toString()}`;
      const res = await axios.get(url);
      setRows(res.data?.rows || []);
      writeInstantCache('withdraw_report', 'all', res.data?.rows || []);
      setTotals(res.data?.totals || {});
      setLastRefreshedAt(new Date());
      if (opts.userTriggered) toast.success('Report refreshed');
    } catch (e) {
      if (!opts.background) toast.error('Failed to load report');
    }
    finally { if (!opts.background) setLoading(false); }
  };

  useEffect(() => { if (open) load({ background: !!readInstantCache('withdraw_report', 'all') }); /* eslint-disable-next-line */ }, [open, view, activeCustomer, status, rangeType, customMode, fromDate, toDate, mbSetup]);

  // Auto-refresh in the background so edits made from a chat window
  // (Edit Deposit / Edit Withdraw modal, Mark-as-Paid, Cancel) show up here
  // without the admin having to close and reopen the report.
  // BIG-PLAN Point 3 — relaxed 4s → 30s. Users can still tap the manual
  // Refresh button in the header for an instant pull.
  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => { load({ background: true }); }, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [open, view, activeCustomer, status, rangeType, customMode, fromDate, toDate, mbSetup]);

  // Point 34 — same preview flow now drives both Excel and PDF downloads;
  // `previewFormat` is null (closed) or the format the preview is for.
  const [previewFormat, setPreviewFormat] = useState(null);

  // Fix doc #2 — Settings back button steps back through this modal's own
  // internal views (export preview → fullscreen → client detail → list)
  // before the parent is allowed to exit to the Settings grid.
  useEffect(() => {
    const onBack = (ev) => {
      if (previewFormat) { setPreviewFormat(null); ev.preventDefault(); return; }
      if (fullView) { setFullView(false); ev.preventDefault(); return; }
      if (view === 'client') { setView('list'); setActiveCustomer(null); ev.preventDefault(); return; }
    };
    window.addEventListener('dws:settings-back', onBack);
    return () => window.removeEventListener('dws:settings-back', onBack);
  }, [previewFormat, fullView, view]);

  const downloadWithColumns = ({ columns }) => {
    const p = buildParams();
    p.set('format', previewFormat || 'xlsx');
    if (columns) p.set('columns', columns);
    const base = status === 'enquiry' ? 'inquiry-entries' : 'withdraw-entries';
    const url = `${API}/${base}/report/export?${p.toString()}`;
    window.open(url, '_blank');
    setPreviewFormat(null);
  };

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const s = q.toLowerCase();
    return rows.filter(r => (r.customer_username || '').toLowerCase().includes(s));
  }, [rows, q]);

  const fmt = (n) => `₹${(n || 0).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', maximumFractionDigits: 0 })}`;
  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-2" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`bg-white rounded-xl w-full ${fullView ? 'max-w-[98vw] h-[96vh]' : 'max-w-3xl max-h-[90vh]'} flex flex-col overflow-hidden shadow-2xl`} data-testid="withdraw-report-modal">
        <div className="px-4 py-3 border-b flex items-center justify-between bg-blue-700 text-white">
          <div className="flex items-center gap-2">
            {view === 'client' && (
              <button onClick={() => { setView('list'); setActiveCustomer(null); }} className="p-1 hover:bg-white/20 rounded-full"><ArrowLeft size={18} /></button>
            )}
            <h3 className="text-base font-bold">
              {view === 'client' ? `${activeCustomer?.username} · History` : 'Reports (All Clients)'}
            </h3>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-full"><X size={20} /></button>
        </div>

        {/* Filters */}
        <div className="p-3 border-b bg-gray-50 space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {/* P16 — standalone Withdraw tab + requested order:
                All, Withdraw, Deposit, Enquiry, Deposit+Withdraw, Success,
                Pending, Cancelled. ('withdraw' falls to the backend default
                = all withdraws.) */}
            {['all', 'withdraw', 'deposit', 'enquiry', 'combined', 'paid', 'pending', 'cancelled'].map(s => (
              <button key={s} onClick={() => setStatus(s)}
                data-testid={`report-status-${s}`}
                className={`px-2.5 py-1 rounded text-[11px] font-semibold border capitalize ${status === s ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-gray-200 text-gray-700'}`}>
                {s === 'paid' ? 'Success' : s === 'combined' ? 'Deposit + Withdraw' : s}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-1.5 items-center">
            {['today', 'yesterday', 'weekly', 'monthly', 'custom'].map(r => (
              <button key={r} onClick={() => setRangeType(r)}
                data-testid={`report-range-${r}`}
                className={`px-2.5 py-1 rounded text-[11px] font-semibold border capitalize ${rangeType === r ? 'bg-purple-600 text-white border-purple-600' : 'bg-white border-gray-200 text-gray-700'}`}>
                {r}
              </button>
            ))}
            <button
              onClick={() => load({ userTriggered: true })}
              disabled={loading}
              data-testid="report-refresh-btn"
              className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-semibold border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
              title="Refresh — pull latest edits into the report"
            >
              <ArrowsClockwise size={12} weight="bold" className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
            {lastRefreshedAt && (
              <span className="text-[10px] text-slate-400" data-testid="report-last-refreshed">
                {lastRefreshedAt.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </span>
            )}
          </div>
          {rangeType === 'custom' && (
            <div className="flex flex-col gap-2">
              <div className="flex gap-1.5">
                <button onClick={() => setCustomMode('single')} data-testid="report-custom-single"
                  className={`px-2.5 py-1 rounded text-[11px] font-semibold border ${customMode === 'single' ? 'bg-purple-600 text-white border-purple-600' : 'bg-white border-gray-200 text-gray-700'}`}>Single date</button>
                <button onClick={() => setCustomMode('range')} data-testid="report-custom-range"
                  className={`px-2.5 py-1 rounded text-[11px] font-semibold border ${customMode === 'range' ? 'bg-purple-600 text-white border-purple-600' : 'bg-white border-gray-200 text-gray-700'}`}>From – To</button>
              </div>
              {customMode === 'single' ? (
                <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-full px-2 py-1 border rounded text-xs" data-testid="report-single-date" />
              ) : (
                <div className="flex gap-2 items-center">
                  <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="flex-1 px-2 py-1 border rounded text-xs" data-testid="report-from-date" />
                  <span className="text-[10px] text-gray-500">to</span>
                  <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="flex-1 px-2 py-1 border rounded text-xs" data-testid="report-to-date" />
                </div>
              )}
            </div>
          )}

          {/* MB-wise filter — All MB / MB7-MB11 */}
          <div className="flex flex-wrap gap-1.5 items-center">
            <span className="text-[10px] font-semibold uppercase text-slate-500 mr-1">MB:</span>
            {mbOptions.map((m) => (
              <button
                key={m}
                onClick={() => setMbSetup(m)}
                data-testid={`report-mb-${m}`}
                className={`px-2.5 py-1 rounded text-[11px] font-semibold border uppercase ${mbSetup === m ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white border-gray-200 text-gray-700'}`}
              >
                {m === 'all' ? 'All MB' : m}
              </button>
            ))}
          </div>
          {view === 'list' && (
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-2 text-gray-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search client name..."
                className="w-full pl-8 pr-2 py-1.5 border rounded text-xs"
                data-testid="report-search-input" />
            </div>
          )}
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="py-10 text-center text-gray-400 text-sm">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="py-10 text-center text-gray-400 text-sm">No entries</div>
          ) : status === 'enquiry' ? (
            /* P17 — enriched Enquiry table: First Name/Time · Registered
               Name/Time (or auto Closed / No Reply after 2h) · Time Between ·
               Registered ID · Mobile · Registered By. */
            <table className={`w-full ${fullView ? 'text-sm' : 'text-xs'}`} data-testid="enquiry-report-table">
              <thead className={`sticky top-0 ${fullView ? 'bg-purple-700 text-white' : 'bg-purple-100 text-purple-800'}`}>
                <tr>
                  <th className="px-3 py-2 text-left">#</th>
                  <th className="px-3 py-2 text-left">First Name / Time</th>
                  <th className="px-3 py-2 text-left">Registered / Status</th>
                  <th className="px-3 py-2 text-left">Time Between</th>
                  <th className="px-3 py-2 text-left">Registered ID</th>
                  <th className="px-3 py-2 text-left">Mobile</th>
                  <th className="px-3 py-2 text-left">Registered By</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => {
                  const fmt = (iso) => {
                    if (!iso) return '-';
                    try { return new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true }); }
                    catch { return iso; }
                  };
                  const tb = (secs) => {
                    if (secs == null) return '-';
                    const m = Math.floor(secs / 60);
                    return `${m} minute${m === 1 ? '' : 's'}`;
                  };
                  const st = r.registered_status || 'Open';
                  const stColor = st === 'Registered' ? 'text-emerald-700 bg-emerald-50'
                    : st === 'No Reply' ? 'text-red-700 bg-red-50'
                    : st === 'Closed' ? 'text-slate-600 bg-slate-100'
                    : 'text-amber-700 bg-amber-50';
                  return (
                    <tr key={r.id || i} className="border-b border-gray-100 hover:bg-purple-50/30 align-top" data-testid={`enquiry-report-row-${i}`}>
                      <td className="px-3 py-1.5 text-gray-500">{i + 1}</td>
                      <td className="px-3 py-1.5">
                        <div className="font-semibold text-slate-800">{r.first_name || '-'}</div>
                        <div className="text-[10px] font-mono text-slate-400">{fmt(r.started_at)}</div>
                      </td>
                      <td className="px-3 py-1.5">
                        {r.registered_name ? (
                          <>
                            <div className="font-semibold text-emerald-700">{r.registered_name}</div>
                            <div className="text-[10px] font-mono text-slate-400">{fmt(r.registered_at)}</div>
                          </>
                        ) : (
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${stColor}`}>{st}</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 font-mono font-semibold text-purple-700">{tb(r.time_between_seconds)}</td>
                      <td className="px-3 py-1.5 font-mono text-[11px] font-semibold text-slate-700">{r.registered_id || '-'}</td>
                      <td className="px-3 py-1.5 font-mono text-[11px]">{r.mobile || '-'}</td>
                      <td className="px-3 py-1.5 text-[11px] text-slate-600">{r.registered_by_name || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <table className={`w-full ${fullView ? 'text-sm' : 'text-xs'}`} data-testid="report-table">
              <thead className={`sticky top-0 ${fullView ? 'bg-emerald-700 text-white' : 'bg-gray-100 text-gray-700'}`}>
                <tr>
                  <th className={`px-2 py-2 text-left ${fullView ? 'border border-emerald-900' : ''}`}>#</th>
                  {/* Session-48g: New "Client Code" column right after # */}
                  <th className={`px-2 py-2 text-left ${fullView ? 'border border-emerald-900' : ''}`}>Client Code</th>
                  <th className={`px-2 py-2 text-left ${fullView ? 'border border-emerald-900' : ''}`}>Username</th>
                  <th className={`px-2 py-2 text-left ${fullView ? 'border border-emerald-900' : ''}`}>Type</th>
                  <th className={`px-2 py-2 text-right ${fullView ? 'border border-emerald-900' : ''}`}>Amount</th>
                  <th className={`px-2 py-2 text-left ${fullView ? 'border border-emerald-900' : ''}`}>Date</th>
                  <th className={`px-2 py-2 text-left ${fullView ? 'border border-emerald-900' : ''}`}>Time</th>
                  <th className={`px-2 py-2 text-left ${fullView ? 'border border-emerald-900' : ''}`}>Status</th>
                  <th className={`px-2 py-2 text-left ${fullView ? 'border border-emerald-900' : ''}`}>UTR</th>
                  <th className={`px-2 py-2 text-left ${fullView ? 'border border-emerald-900' : ''}`}>Reference</th>
                  <th className={`px-2 py-2 text-left ${fullView ? 'border border-emerald-900' : ''}`}>
                    Received From
                    <div className="text-[9px] opacity-70 font-normal">Client Account</div>
                  </th>
                  <th className={`px-2 py-2 text-left ${fullView ? 'border border-emerald-900' : ''}`}>
                    Received In
                    <div className="text-[9px] opacity-70 font-normal">Our Account</div>
                  </th>
                  <th className={`px-2 py-2 text-left ${fullView ? 'border border-emerald-900' : ''}`}>
                    Paid To
                    <div className="text-[9px] opacity-70 font-normal">Client Account</div>
                  </th>
                  <th className={`px-2 py-2 text-left ${fullView ? 'border border-emerald-900' : ''}`}>
                    Paid From
                    <div className="text-[9px] opacity-70 font-normal">Our Account</div>
                  </th>
                  <th className={`px-2 py-2 text-left ${fullView ? 'border border-emerald-900' : ''}`}>Description</th>
                  <th className={`px-2 py-2 text-left ${fullView ? 'border border-emerald-900' : ''}`}>
                    Client Address
                    <div className="text-[9px] opacity-70 font-normal">from IFSC</div>
                  </th>
                  {/* Session-48d Reports Upgrade K: 3 timestamps + 2 TB columns.
                      TB1 = Created → First Handler Reply (customer wait).
                      TB2 = First Reply → Payment (staff-to-money gap).
                      + withdraw-only account snapshot (frozen at approval). */}
                  <th className={`px-2 py-2 text-left ${fullView ? 'border border-emerald-900' : ''}`}>
                    Created
                    <div className="text-[9px] opacity-70 font-normal">Customer</div>
                  </th>
                  <th className={`px-2 py-2 text-left ${fullView ? 'border border-emerald-900' : ''}`}>
                    First Reply
                    <div className="text-[9px] opacity-70 font-normal">Handler</div>
                  </th>
                  <th className={`px-2 py-2 text-left ${fullView ? 'border border-emerald-900' : ''}`}>
                    Closed
                  </th>
                  <th className={`px-2 py-2 text-left ${fullView ? 'border border-emerald-900' : ''}`}>
                    TB1
                    <div className="text-[9px] opacity-70 font-normal">Create→FirstReply</div>
                  </th>
                  <th className={`px-2 py-2 text-left ${fullView ? 'border border-emerald-900' : ''}`}>
                    TB2
                    <div className="text-[9px] opacity-70 font-normal">FirstReply→Close</div>
                  </th>
                  <th className={`px-2 py-2 text-left ${fullView ? 'border border-emerald-900' : ''}`}>
                    TB3
                    <div className="text-[9px] opacity-70 font-normal">Create→Close</div>
                  </th>
                  <th className={`px-2 py-2 text-left ${fullView ? 'border border-emerald-900' : ''}`}>Account Number</th>
                  <th className={`px-2 py-2 text-left ${fullView ? 'border border-emerald-900' : ''}`}>IFSC Code</th>
                  <th className={`px-2 py-2 text-left ${fullView ? 'border border-emerald-900' : ''}`}>Account Holder</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => {
                  // Use effective_at: paid_at for paid rows, cancelled_at for
                  // cancelled ones, created_at otherwise — displayed in IST.
                  const rawTs = r.effective_at || r.created_at || '';
                  let date = '', time = '';
                  if (rawTs) {
                    try {
                      const d = new Date(rawTs.endsWith('Z') || rawTs.includes('+') ? rawTs : rawTs + 'Z');
                      date = d.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' });
                      time = d.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true });
                    } catch (_) {
                      date = rawTs.slice(0, 10); time = rawTs.slice(11, 16);
                    }
                  }
                  // Session-32 timing columns — compact IST label + T/B gap.
                  // Deposits leave these blank (no request→approve cycle).
                  const fmtTsIst = (ts) => {
                    if (!ts) return '';
                    try {
                      const d = new Date(String(ts).endsWith('Z') || String(ts).includes('+') ? ts : ts + 'Z');
                      const dd = d.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: '2-digit' });
                      const tt = d.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true });
                      return `${dd} ${tt}`;
                    } catch (_) { return String(ts).slice(0, 16); }
                  };
                  // Point 38 — Time Between is always shown in minutes, never
                  // seconds; anything under a minute rounds down to 0.
                  const timeBetween = (a, b) => {
                    if (!a || !b) return '-';
                    try {
                      const A = new Date(String(a).endsWith('Z') || String(a).includes('+') ? a : a + 'Z').getTime();
                      const B = new Date(String(b).endsWith('Z') || String(b).includes('+') ? b : b + 'Z').getTime();
                      const secs = Math.floor((B - A) / 1000);
                      if (secs < 0 || isNaN(secs)) return '-';
                      const mins = Math.floor(secs / 60);
                      return `${mins}m`;
                    } catch (_) { return '-'; }
                  };
                  const reqTimeCell = r.type === 'deposit' ? '' : fmtTsIst(r.created_at);
                  const paidTimeCell = r.type === 'deposit' ? '' : fmtTsIst(r.paid_at);
                  const tbCell = r.type === 'deposit' ? '' : (r.status === 'paid' ? timeBetween(r.created_at, r.paid_at) : '-');
                  // Session-48d Reports Upgrade K — the 3 timestamps + 2 TB
                  // columns. sr_started_at is the customer-request time
                  // captured at coin-cut/deposit time; falls back to
                  // created_at for legacy rows without an SR link.
                  const createdTs = r.sr_started_at || r.created_at || '';
                  const approvedTs = r.approved_at || r.created_at || '';
                  const closedTs = r.type === 'deposit'
                    ? (r.closed_at || r.approved_at || r.created_at || '')
                    : (r.status === 'paid' ? (r.paid_at || '') : '');
                  const createdCell = fmtTsIst(createdTs);
                  const approvedCell = fmtTsIst(approvedTs);
                  const closedCell = closedTs ? fmtTsIst(closedTs) : '';
                  const tb1Cell = (createdTs && approvedTs && createdTs !== approvedTs) ? timeBetween(createdTs, approvedTs) : '-';
                  const tb2Cell = (approvedTs && closedTs && approvedTs !== closedTs) ? timeBetween(approvedTs, closedTs) : '-';
                  const tb3Cell = (createdTs && closedTs && createdTs !== closedTs) ? timeBetween(createdTs, closedTs) : '-';
                  const isWithdraw = r.type !== 'deposit';
                  const bs = r.bank_snapshot || {};
                  const accNoCell = isWithdraw ? (bs.bank_account_number || '-') : '';
                  const accIfscCell = isWithdraw ? (bs.ifsc_code || '-') : '';
                  const accHolderCell = isWithdraw ? (bs.account_holder_name || '-') : '';
                  const isDeposit = r.type === 'deposit';
                  const effStatus = isDeposit ? 'done' : (r.status || '');
                  const badge = effStatus === 'paid'
                    ? 'bg-green-100 text-green-800'
                    : effStatus === 'cancelled'
                      ? 'bg-red-100 text-red-800'
                      : effStatus === 'done'
                        ? 'bg-sky-100 text-sky-800'
                        : 'bg-amber-100 text-amber-800';
                  const rowBg = fullView ? (i % 2 === 0 ? 'bg-white' : 'bg-emerald-50/40') : '';
                  const cellBorder = fullView ? 'border border-gray-200' : '';
                  const refCell = r.paid_reference || r.reference || '-';
                  // Split the single "Bank" cell into 4 semantic cells so
                  // the View matches the Excel one-to-one.
                  const receivedFrom = isDeposit ? (r.received_from || '') : '';
                  const receivedIn = isDeposit ? (r.received_in || r.bank_account || '') : '';
                  const paidTo = !isDeposit
                    ? ((r.bank_snapshot && r.bank_snapshot.bank_name) || r.paid_bank_account || r.bank_account || '')
                    : '';
                  const paidFrom = !isDeposit ? (r.paid_from_bank || '') : '';
                  const descCell = r.description || '-';
                  const addressCell = r.client_address_text
                    || (r.client_address && r.client_address.formatted)
                    || '-';
                  const jumpToChat = () => {
                    // Deep-link to the exact chat + message. Admin routes to
                    // AdminChat, handler routes to HandlerChat. `msgId` in the
                    // hash tells the chat page to scroll + highlight.
                    if (!r.customer_id) return;
                    const isAdmin = actorRole === 'super_admin';
                    // Prefer room_id when known; else fall back to type-based
                    // routing so we still land in the right chat.
                    const roomId = r.room_id || null;
                    const msgId = r.message_id || r.group_message_id || '';
                    const chatType = r.type === 'deposit' ? 'deposit' : 'withdrawal';
                    const base = isAdmin ? '/admin-chat' : '/handler-chat';
                    const target = roomId ? `${base}/${roomId}` : `${base}/customer/${r.customer_id}?type=${chatType}`;
                    onClose && onClose();
                    setTimeout(() => {
                      navigate(msgId ? `${target}#msg-${msgId}` : target);
                    }, 60);
                  };
                  return (
                    <tr
                      key={r.id}
                      className={`border-t hover:bg-blue-50/60 cursor-pointer ${rowBg}`}
                      onClick={jumpToChat}
                      data-testid={`report-row-${r.id}`}
                      title="Click to open this entry in the chat"
                    >
                      <td className={`px-2 py-1.5 text-gray-500 ${cellBorder}`}>{i + 1}</td>
                      {/* Session-48g: Client Code cell (falls back to '-' for legacy rows) */}
                      <td className={`px-2 py-1.5 font-mono text-[11px] font-semibold text-slate-700 ${cellBorder}`} data-testid={`report-client-code-${r.id}`}>{r.client_code || '-'}</td>
                      <td className={`px-2 py-1.5 ${cellBorder}`}>
                        {view === 'list' ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); setView('client'); setActiveCustomer({ id: r.customer_id, username: r.customer_username }); }}
                            data-testid={`report-drill-${r.customer_id}`}
                            className="text-blue-600 hover:underline font-semibold"
                          >{r.customer_username}</button>
                        ) : r.customer_username}
                        {r.customer_mb && r.customer_mb !== '-' && (
                          <span
                            className="ml-1.5 inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-indigo-100 text-indigo-700 align-middle"
                            data-testid={`report-mb-tag-${r.id}`}
                            title={`Customer belongs to ${r.customer_mb}`}
                          >
                            {r.customer_mb}
                          </span>
                        )}
                      </td>
                      <td className={`px-2 py-1.5 ${cellBorder}`}>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${isDeposit ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'}`}>{isDeposit ? 'Deposit' : 'Withdraw'}</span>
                      </td>
                      <td className={`px-2 py-1.5 text-right font-mono font-bold ${cellBorder}`}>₹{r.amount}</td>
                      <td className={`px-2 py-1.5 ${cellBorder}`}>{date}</td>
                      <td className={`px-2 py-1.5 ${cellBorder}`}>{time}</td>
                      <td className={`px-2 py-1.5 ${cellBorder}`}><span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${badge}`}>{effStatus === 'paid' ? 'Success' : effStatus}</span></td>
                      <td className={`px-2 py-1.5 font-mono text-[10px] ${cellBorder}`}>{r.utr_number || '-'}</td>
                      <td className={`px-2 py-1.5 text-[11px] ${cellBorder}`}>{refCell}</td>
                      <td className={`px-2 py-1.5 text-[11px] ${cellBorder}`}>{receivedFrom || '-'}</td>
                      <td className={`px-2 py-1.5 text-[11px] ${cellBorder}`}>{receivedIn || '-'}</td>
                      <td className={`px-2 py-1.5 text-[11px] ${cellBorder}`}>{paidTo || '-'}</td>
                      <td className={`px-2 py-1.5 text-[11px] ${cellBorder}`}>{paidFrom || '-'}</td>
                      <td className={`px-2 py-1.5 text-[11px] ${cellBorder}`}>{descCell}</td>
                      <td className={`px-2 py-1.5 text-[11px] text-blue-700 ${cellBorder}`} title={r.client_address?.formatted || ''}>{addressCell}</td>
                      {/* Session-48d Reports Upgrade K — 3 timestamps + 2 TB
                          + 3 account snapshot columns (withdraws only). */}
                      <td className={`px-2 py-1.5 text-[10px] font-mono ${cellBorder}`} data-testid={`report-created-${r.id}`}>{createdCell || '-'}</td>
                      <td className={`px-2 py-1.5 text-[10px] font-mono ${cellBorder}`} data-testid={`report-approved-${r.id}`}>{approvedCell || '-'}</td>
                      <td className={`px-2 py-1.5 text-[10px] font-mono ${cellBorder}`} data-testid={`report-closed-${r.id}`}>{closedCell || '-'}</td>
                      <td className={`px-2 py-1.5 text-[10px] font-mono font-semibold text-blue-700 ${cellBorder}`} data-testid={`report-tb1-${r.id}`}>{tb1Cell}</td>
                      <td className={`px-2 py-1.5 text-[10px] font-mono font-semibold text-emerald-700 ${cellBorder}`} data-testid={`report-tb2-${r.id}`}>{tb2Cell}</td>
                      <td className={`px-2 py-1.5 text-[10px] font-mono font-semibold text-purple-700 ${cellBorder}`} data-testid={`report-tb3-${r.id}`}>{tb3Cell}</td>
                      <td className={`px-2 py-1.5 text-[11px] font-mono ${cellBorder}`} data-testid={`report-acc-no-${r.id}`}>{accNoCell}</td>
                      <td className={`px-2 py-1.5 text-[11px] font-mono ${cellBorder}`} data-testid={`report-acc-ifsc-${r.id}`}>{accIfscCell}</td>
                      <td className={`px-2 py-1.5 text-[11px] ${cellBorder}`} data-testid={`report-acc-holder-${r.id}`}>{accHolderCell}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Totals + Export */}
        <div className="border-t bg-gray-50 p-3">
          <div className="grid grid-cols-3 gap-2 text-[11px] mb-2">
            <div className="rounded bg-green-50 border border-green-200 p-2">
              <div className="font-bold text-green-800">Success</div>
              <div className="text-green-900 font-bold">{fmt(totals.success_amount)}</div>
              <div className="text-green-700">{totals.success_count || 0} entries</div>
            </div>
            <div className="rounded bg-amber-50 border border-amber-200 p-2">
              <div className="font-bold text-amber-800">Pending</div>
              <div className="text-amber-900 font-bold">{fmt(totals.pending_amount)}</div>
              <div className="text-amber-700">{totals.pending_count || 0} entries</div>
            </div>
            <div className="rounded bg-red-50 border border-red-200 p-2">
              <div className="font-bold text-red-800">Cancelled</div>
              <div className="text-red-900 font-bold">{fmt(totals.cancelled_amount)}</div>
              <div className="text-red-700">{totals.cancelled_count || 0} entries</div>
            </div>
          </div>
          <div className="flex gap-2">
            {actorRole === 'super_admin' && (
              <ClearReportRangeButton adminId={user?.id} onCleared={load} />
            )}
            <button onClick={() => setFullView(v => !v)} data-testid="report-view-toggle"
              className="flex-1 py-2 bg-slate-700 text-white rounded text-sm font-bold flex items-center justify-center gap-1.5 hover:bg-slate-800">
              {fullView ? '✕ Close View' : '👁  View'}
            </button>
            <button onClick={() => setPreviewFormat('xlsx')} data-testid="report-download-excel"
              className="flex-1 py-2 bg-green-600 text-white rounded text-sm font-bold flex items-center justify-center gap-1.5 hover:bg-green-700">
              <DownloadSimple size={14} weight="bold" /> Excel
            </button>
            <button onClick={() => setPreviewFormat('pdf')} data-testid="report-download-pdf"
              className="flex-1 py-2 bg-red-600 text-white rounded text-sm font-bold flex items-center justify-center gap-1.5 hover:bg-red-700">
              <DownloadSimple size={14} weight="bold" /> PDF
            </button>
          </div>
        </div>
      </div>
      <ExcelExportPreviewModal
        open={!!previewFormat}
        format={previewFormat || 'xlsx'}
        onClose={() => setPreviewFormat(null)}
        rows={filtered}
        onDownload={downloadWithColumns}
        isAdmin={actorRole === 'super_admin'}
      />
    </div>
  );
};

export default WithdrawReportModal;
